import React, { useState, useEffect, useRef, useId } from "react";
import { Search, Building2, X, Check } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import {
  companyPickerState,
  companyPickerHint,
  PICKER_UNRESOLVED,
} from "utilities/companyPicker";

/**
 * CompanyPickerField
 * -------------------------------------------------------
 * Debounced company picker intended for use inside filter drawers.
 * Mirrors LinkParentCompanyModal's search pattern but is inline and
 * emits `(companyId, companyName)` via onChange rather than persisting.
 *
 * Props:
 *   - label: field label
 *   - companyType: filter the server query (e.g. "partner")
 *   - value: current selected companyId (string or "")
 *   - valueLabel: display label for the current selection (optional)
 *   - onChange({ id, name }): called when user picks or clears
 *   - disabled: hides the control when true
 */
export default function CompanyPickerField({
  label = "Company",
  companyType,
  value,
  valueLabel,
  onChange,
  disabled = false,
}) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvedLabel, setResolvedLabel] = useState(valueLabel || "");

  const debounceRef = useRef(null);
  const rootRef = useRef(null);
  // Stable per-instance id: several pickers can share a drawer (Client on /mids,
  // Partner on /companies), so the hint's aria-describedby target must be unique.
  const fieldId = useId();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // Resolve label when value exists but we have no label yet
  useEffect(() => {
    if (valueLabel) {
      setResolvedLabel(valueLabel);
      return;
    }
    if (!value || !effectiveCompanyId || !auth?.user) return;
    let cancelled = false;
    makeApiRequest(
      "get",
      apiRoutes.getCompany(value),
      auth.user,
      effectiveCompanyId
    )
      .then((res) => {
        if (cancelled) return;
        // GET /companies/:id returns the company object directly on res.data
        // (see hooks/useCompany.jsx).
        const body = res?.data || {};
        if (body?.name) setResolvedLabel(body.name);
      })
      .catch(() => {
        if (!cancelled) setResolvedLabel("");
      });
    return () => {
      cancelled = true;
    };
  }, [value, valueLabel, effectiveCompanyId, auth?.user]);

  // Debounced search while open
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCandidates(query);
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  const fetchCandidates = async (search) => {
    if (!effectiveCompanyId || !auth?.user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyType) params.set("companyType", companyType);
      params.set("limit", "25");
      if (search) params.set("search", search);

      const res = await makeApiRequest(
        "get",
        `${apiRoutes.getCompanies}?${params.toString()}`,
        auth.user,
        effectiveCompanyId
      );
      const all = Array.isArray(res?.data?.data) ? res.data.data : [];
      setResults(all);
    } catch (err) {
      // swallow; show empty state
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  if (disabled) return null;

  // SOUND-680: `query` is local state and only becomes a filter when a result is
  // CLICKED. Typing a name and hitting Apply Filters applied nothing, silently.
  const pickerState = companyPickerState({ value, query });
  const hint = companyPickerHint(pickerState);

  const baseInput =
    "w-full bg-card text-foreground border border-border rounded-md px-3 py-2 " +
    "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand " +
    "transition";

  return (
    <div className="flex flex-col text-sm text-foreground" ref={rootRef}>
      <span className="font-medium">{label}</span>
      {/* Selected chip or picker trigger */}
      {value ? (
        <div className="mt-1 flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-foreground truncate flex-1">
            {resolvedLabel || value}
          </span>
          <button
            type="button"
            onClick={() => {
              onChange?.({ id: "", name: "" });
              setResolvedLabel("");
              setQuery("");
            }}
            className="btn btn-ghost h-6 w-6 p-0"
            aria-label="Clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              className={`${baseInput} pl-9 ${
                pickerState === PICKER_UNRESOLVED
                  ? "border-warning focus:border-warning focus:ring-warning/30"
                  : ""
              }`}
              placeholder="Search by name or slug..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setOpen(true)}
              aria-describedby={hint ? `${fieldId}-hint` : undefined}
            />
          </div>
          {hint && (
            <p
              id={`${fieldId}-hint`}
              role="status"
              className="mt-1 text-xs text-warning"
            >
              {hint}
            </p>
          )}
          {open && (
            <div className="relative mt-1">
              <div className="absolute left-0 right-0 z-10 rounded-md border border-border bg-card shadow-lg max-h-64 overflow-y-auto">
                {loading ? (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    Searching...
                  </div>
                ) : results.length === 0 ? (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    {query ? "No companies found." : "Start typing to search."}
                  </div>
                ) : (
                  <ul className="py-1">
                    {results.map((c) => (
                      <li key={c.companyId}>
                        <button
                          type="button"
                          onClick={() => {
                            onChange?.({ id: c.companyId, name: c.name });
                            setResolvedLabel(c.name || "");
                            setOpen(false);
                            setQuery("");
                          }}
                          className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-muted/60"
                        >
                          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">
                              {c.name}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {c.slug ? `${c.slug} · ` : ""}
                              {c.companyId}
                            </div>
                          </div>
                          <Check className="h-4 w-4 text-brand opacity-0 group-hover:opacity-100" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
